import React, { useState } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Sparkles, CheckCircle2, Loader2, Mail } from 'lucide-react';

export function LeadCapture({ magnet, page, title, subtitle, variant = 'inline', className = '' }) {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    try {
      const API = process.env.REACT_APP_BACKEND_URL;
      await fetch(`${API}/api/leads/capture`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, name, magnet, page }),
      });
      setSubmitted(true);
    } catch (e) { /* handled */ } finally { setLoading(false); }
  };

  if (submitted) {
    return (
      <div className={`text-center py-6 ${className}`} data-testid={`lead-success-${magnet}`}>
        <CheckCircle2 className="w-8 h-8 text-emerald-500 mx-auto mb-2" />
        <p className="text-sm font-bold text-gray-900">You're in!</p>
        <p className="text-xs text-gray-500 mt-1">Check your inbox at {email}</p>
      </div>
    );
  }

  if (variant === 'hero') {
    return (
      <div className={`${className}`} data-testid={`lead-capture-${magnet}`}>
        <h3 className="text-lg font-bold text-white mb-1">{title}</h3>
        <p className="text-sm text-gray-300 mb-4">{subtitle}</p>
        <form onSubmit={submit} className="flex gap-2 max-w-md">
          <Input value={email} onChange={e => setEmail(e.target.value)} type="email" required placeholder="your@email.com" className="flex-1 rounded-lg bg-white/10 border-white/20 text-white placeholder:text-gray-400 h-11" data-testid={`lead-email-${magnet}`} />
          <Button type="submit" disabled={loading} className="bg-[#D4A843] hover:bg-[#c49a3a] text-[#0f172a] rounded-lg h-11 px-5 font-bold text-sm" data-testid={`lead-submit-${magnet}`}>
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Get It Free'}
          </Button>
        </form>
      </div>
    );
  }

  if (variant === 'card') {
    return (
      <div className={`bg-gradient-to-br from-[#0f172a] to-[#1e293b] rounded-xl p-6 text-center ${className}`} data-testid={`lead-capture-${magnet}`}>
        <Sparkles className="w-6 h-6 text-[#D4A843] mx-auto mb-2" />
        <h3 className="text-base font-bold text-white mb-1">{title}</h3>
        <p className="text-xs text-gray-400 mb-4">{subtitle}</p>
        <form onSubmit={submit} className="space-y-2 max-w-xs mx-auto">
          <Input value={name} onChange={e => setName(e.target.value)} placeholder="Your name" className="rounded-lg bg-white/10 border-white/20 text-white placeholder:text-gray-400 text-sm h-9" />
          <Input value={email} onChange={e => setEmail(e.target.value)} type="email" required placeholder="your@email.com" className="rounded-lg bg-white/10 border-white/20 text-white placeholder:text-gray-400 text-sm h-9" data-testid={`lead-email-${magnet}`} />
          <Button type="submit" disabled={loading} className="w-full bg-[#D4A843] hover:bg-[#c49a3a] text-[#0f172a] rounded-lg h-9 font-bold text-xs" data-testid={`lead-submit-${magnet}`}>
            {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : title}
          </Button>
        </form>
      </div>
    );
  }

  // Default: inline banner
  return (
    <div className={`bg-[#0f172a] rounded-xl p-5 flex flex-col md:flex-row items-center gap-4 ${className}`} data-testid={`lead-capture-${magnet}`}>
      <div className="flex items-center gap-3 flex-1">
        <div className="w-10 h-10 bg-[#D4A843]/20 rounded-full flex items-center justify-center shrink-0"><Mail className="w-5 h-5 text-[#D4A843]" /></div>
        <div>
          <h3 className="text-sm font-bold text-white">{title}</h3>
          <p className="text-[10px] text-gray-400">{subtitle}</p>
        </div>
      </div>
      <form onSubmit={submit} className="flex gap-2 shrink-0">
        <Input value={email} onChange={e => setEmail(e.target.value)} type="email" required placeholder="your@email.com" className="rounded-lg bg-white/10 border-white/20 text-white placeholder:text-gray-400 text-xs h-9 w-48" data-testid={`lead-email-${magnet}`} />
        <Button type="submit" disabled={loading} className="bg-[#D4A843] hover:bg-[#c49a3a] text-[#0f172a] rounded-lg h-9 px-4 font-bold text-xs" data-testid={`lead-submit-${magnet}`}>
          {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : 'Subscribe'}
        </Button>
      </form>
    </div>
  );
}

// Exit Intent Popup
export function ExitIntentPopup() {
  const [show, setShow] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  React.useEffect(() => {
    if (localStorage.getItem('exit_intent_shown')) return;
    const handler = (e) => {
      if (e.clientY < 10 && !dismissed) {
        setShow(true);
        localStorage.setItem('exit_intent_shown', 'true');
      }
    };
    document.addEventListener('mouseleave', handler);
    return () => document.removeEventListener('mouseleave', handler);
  }, [dismissed]);

  if (!show || dismissed) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 animate-fade-in" data-testid="exit-intent">
      <div className="bg-white rounded-2xl p-8 max-w-md mx-4 shadow-2xl relative">
        <button onClick={() => { setDismissed(true); setShow(false); }} className="absolute top-3 right-3 text-gray-400 hover:text-gray-600 text-lg">x</button>
        <LeadCapture
          magnet="first_service_discount"
          page="exit_intent"
          title="Wait — 10% Off Your First Service"
          subtitle="New to TechOps? Enter your email for an exclusive discount on any IT or cybersecurity service."
          variant="card"
        />
      </div>
    </div>
  );
}
